import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, CalendarRange, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return {
    tahun_ajaran: "", semester: "ganjil",
    tanggal_mulai: "", tanggal_selesai: "",
    is_active: false, keterangan: "",
  };
}

export default function TahunAjaranPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tahun-ajaran");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      tahun_ajaran: it.tahun_ajaran || "", semester: it.semester || "ganjil",
      tanggal_mulai: it.tanggal_mulai || "", tanggal_selesai: it.tanggal_selesai || "",
      is_active: !!it.is_active, keterangan: it.keterangan || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/tahun-ajaran/${editing.id}`, form);
        toast.success("Tahun ajaran diperbarui");
      } else {
        await api.post("/tahun-ajaran", form);
        toast.success("Tahun ajaran ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/tahun-ajaran/${deleteTarget.id}`);
      toast.success("Tahun ajaran dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="tahun-ajaran-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Sistem</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Tahun Ajaran</h1>
        <p className="text-slate-500 text-sm">Kelola periode tahun ajaran dan semester. Hanya satu yang aktif.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex justify-end">
          <Button onClick={openCreate} data-testid="add-ta-button"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2">
            <Plus className="h-4 w-4" /> Tambah Tahun Ajaran
          </Button>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tahun Ajaran</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Semester</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Periode</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Keterangan</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                  Belum ada tahun ajaran.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`ta-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <CalendarRange className="h-4 w-4" />
                      </div>
                      <span className="font-semibold text-slate-900">{it.tahun_ajaran}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm capitalize text-slate-700">{it.semester}</TableCell>
                  <TableCell className="text-sm text-slate-700 font-mono text-xs">
                    {it.tanggal_mulai} → {it.tanggal_selesai}
                  </TableCell>
                  <TableCell>
                    {it.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                        <CheckCircle2 className="h-3 w-3" /> Aktif
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">Tidak Aktif</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.keterangan || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-ta-${it.id}`}>
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-ta-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Tahun Ajaran" : "Tambah Tahun Ajaran"}</DialogTitle>
            <DialogDescription>Mengaktifkan periode ini akan menonaktifkan periode lain.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tahun Ajaran *</Label>
                <Input required value={form.tahun_ajaran} onChange={(e) => setForm(f => ({ ...f, tahun_ajaran: e.target.value }))}
                       data-testid="ta-form-tahun" placeholder="2025/2026" />
              </div>
              <div>
                <Label>Semester *</Label>
                <Select value={form.semester} onValueChange={(v) => setForm(f => ({ ...f, semester: v }))}>
                  <SelectTrigger data-testid="ta-form-semester"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ganjil">Ganjil</SelectItem>
                    <SelectItem value="genap">Genap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tanggal Mulai *</Label>
                <Input required type="date" value={form.tanggal_mulai}
                       onChange={(e) => setForm(f => ({ ...f, tanggal_mulai: e.target.value }))}
                       data-testid="ta-form-mulai" />
              </div>
              <div>
                <Label>Tanggal Selesai *</Label>
                <Input required type="date" value={form.tanggal_selesai}
                       onChange={(e) => setForm(f => ({ ...f, tanggal_selesai: e.target.value }))}
                       data-testid="ta-form-selesai" />
              </div>
            </div>
            <div>
              <Label>Keterangan</Label>
              <Textarea rows={2} value={form.keterangan}
                        onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))}
                        data-testid="ta-form-keterangan" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                      data-testid="ta-form-active" />
              <Label>Set sebagai Tahun Ajaran Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ta-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Tahun Ajaran?</AlertDialogTitle>
            <AlertDialogDescription>
              Periode <span className="font-semibold">{deleteTarget?.tahun_ajaran} • {deleteTarget?.semester}</span> akan dihapus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="ta-delete-confirm" className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
